import { HttpClient, HttpErrorResponse } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { environment } from "../../../../environments/environment";
import { ApiErrorHandlerService } from "../../utils/api-error-handler/api-error-handler.service";
import {
  createDefaultThiefProtectionConfig,
  mapDtoToConfig,
  ThiefProtectionAlert,
  ThiefProtectionConfig,
  ThiefProtectionConfigCreate,
  ThiefProtectionConfigDTO,
  ThiefProtectionConfigUpdate,
} from "../../../models/api/thiefProtection";
import { catchError, forkJoin, map, Observable, of, throwError } from "rxjs";
import { ASSET_TYPES, AssetType } from "../../../enums/assetTypes.enum";
import { AssetHierarchyDTO } from "../../../models/api/asset/assetHierarchyDTO";
import { AssetHierarchyService } from "../asset-hierarchy/asset-hierarchy.service";

export interface ThiefProtectionConfigResponse {
  data: ThiefProtectionConfig;
  gates: AssetHierarchyDTO[] | undefined;
  isExisting: boolean;
}

export interface ThiefProtectionAlertResponse {
  data: ThiefProtectionAlert[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

@Injectable({
  providedIn: "root",
})
export class ThiefProtectionService {
  private http = inject(HttpClient);
  private apiErrorHandler = inject(ApiErrorHandlerService);
  private assetHierarchyService = inject(AssetHierarchyService);

  /**
   * Gets the ThiefProtectionConfig for a selected building.
   * @param buildingId The id of the building whose configuration is to be retrieved
   * @returns The ThiefProtectionConfig for the selected building or a default configuration if the building is not yet configured.
   */
  public getThiefProtectionConfig(buildingId: string): Observable<ThiefProtectionConfigResponse | null> {
    const config = this.http
      .get<ThiefProtectionConfigDTO>(`${environment.baseUrlV2}/thiefProtection/buildings/${buildingId}`)
      .pipe(
        map((data) => {
          return { data: mapDtoToConfig(data), isExisting: true };
        }),
        catchError((error: HttpErrorResponse) => {
          if (error.status === 404) {
            return of({
              data: createDefaultThiefProtectionConfig(),
              isExisting: false,
            });
          }

          return throwError(() => error).pipe(this.apiErrorHandler.handle("thief_protection_config", null));
        })
      );
    const gates = this.getGateSystemStatusesByBuilding(buildingId);

    return forkJoin([config, gates]).pipe(
      map(([configResult, gatesResult]) => {
        if (!configResult || !gatesResult) {
          return null;
        }
        return { ...configResult, gates: gatesResult };
      })
    );
  }

  /**
   * Creates a new thief protection configuration for a selected building
   * @param buildingId The id of the building for which the configuration is to be created
   * @param config The configuration that is to be created for the selected building
   * @returns An observable that emits a success response if creation was successfull and null if an error occured
   */
  public createThiefProtectionConfig(buildingId: string, config: ThiefProtectionConfigCreate) {
    return this.http
      .post(`${environment.baseUrlV2}/thiefProtection/buildings/${buildingId}`, config)
      .pipe(this.apiErrorHandler.handle("thief_protection_config", null));
  }

  /**
   * Updates the thief protection configuration for a selected building
   * @param buildingId The id of the building for which the configuration is to be updated
   * @param config The configuration values that are to be updated for the selected building
   * @returns An observable that emits a success response if the update was successfull and null if an error occured
   */
  public updateThiefProtectionConfig(buildingId: string, config: ThiefProtectionConfigUpdate) {
    return this.http
      .put(`${environment.baseUrlV2}/thiefProtection/buildings/${buildingId}`, config)
      .pipe(this.apiErrorHandler.handle("thief_protection_config", null));
  }

  /**
   * Gets a list of all ThiefProtectionAlerts for a specified asset
   * @param assetType The type of the asset whose alerts are to be retrieved
   * @param assetId The id of the asset whose alerts are to be retrieved
   * @param resolved A boolean to decide which alerts are to be retrieved
   * @param page
   * @param limit
   * @returns An observable that emits a list of all retrieved alerts if successfull and an empty array if an error occured
   */
  public getThiefProtectionAlerts(
    assetType: AssetType,
    assetId: string,
    resolved: boolean,
    page: number,
    limit: number
  ): Observable<ThiefProtectionAlertResponse | null> {
    return this.http
      .get<ThiefProtectionAlertResponse>(
        `${environment.baseUrlV2}/thiefProtection/alerts?assetType=${assetType}&assetId=${assetId}&resolved=${resolved}&page=${page}&limit=${limit}`
      )
      .pipe(
        map((data) => ({ data: data.data, pagination: data.pagination })),
        this.apiErrorHandler.handle("thief_protection_alert", null)
      );
  }

  /**
   * Resolves an alert with a provided resolution note
   * @param alertId The id of the alert that is to be resolved
   * @param note The note that is to be set for the alarms resolution
   * @returns An observable that emits a success response if the resolution was succesfull and null if an error occured
   */
  public resolveThiefProtectionAlert(alertId: number, note: string) {
    return this.http
      .put(`${environment.baseUrlV2}/thiefProtection/alerts/${alertId}/resolve`, { resolutionNote: note })
      .pipe(this.apiErrorHandler.handle("thief_protection_alert", null));
  }

  /**
   * Gets the gate systems with their security status for a selected building
   * @param buildingId The id of the building whose gate systems are to be retrieved
   * @returns An Observable that emits the list of gate systems assigned to the selected building if the request was successful and null if an error occured
   */
  public getGateSystemStatusesByBuilding(buildingId: string) {
    return this.assetHierarchyService.getAssetHierarchy(ASSET_TYPES.BUILDING, 2, buildingId).pipe(
      map((data) => {
        return data?.[0]?.children ?? [];
      }),
      this.apiErrorHandler.handle("asset_hierarchy", null)
    );
  }

  /**
   * Activates protection of a selected gate system by creating a security configuration
   * @param gateSystemId The id of the gate system whose configuration is to be created
   * @returns An observable that emits a success response if the creation was succesfull and null if an error occured
   */
  public activateGateSecurity(gateSystemId: string) {
    return this.http
      .post(`${environment.baseUrl}/thiefProtection/gates/${gateSystemId}/securityConfig`, {
        securityPositionThresholdClosed: 5,
        securityPositionThresholdOpen: 95,
      })
      .pipe(this.apiErrorHandler.handle("gate_security", null));
  }

  /**
   * Deactivates protection of a selected gate system by deleting its security configuration
   * @param gateSystemId The id of the gate system whose configuration is to be deleted
   * @returns An observable that emits a success response if the deletion was succesfull and null if an error occured
   */
  public deactivateGateSecurity(gateSystemId: string) {
    return this.http
      .delete(`${environment.baseUrl}/thiefProtection/gates/${gateSystemId}/securityConfig`)
      .pipe(this.apiErrorHandler.handle("gate_security", null));
  }
}
